import dayjs from 'dayjs';
import { createAddress } from '../../createAddress';
import { HpViewportState } from '../../engineClient/types';
import { HeadingAndBody, PolicyModalCopyText } from '../../tools/utilities';
import { Excesses, Policy, SpecifiedItem } from '../../types/policyTypes';
import { createDetailsCard, createExcessesTables, createPolicyWidgetCard } from './policyCommonWidgets';
import { TableWidget } from './tableWidget';

interface PolicyInformationWidgetProps {
  policy: Policy;
  policyModalCopyText: PolicyModalCopyText;
  viewport: HpViewportState;
}

const dateFormat = 'DD MMM YYYY';
const policyReferenceKey = 'Policy reference';
const policyTypeKey = 'Policy type';
const addressKey = 'Address';
const startDateKey = 'Start Date';
const endDateKey = 'End Date';
const buildingsKey = 'Buildings sum insured';
const contentsKey = 'Contents sum insured';
const outbuildingsKey = 'Outbuildings sum insured';
const personalPossessionsKey = 'Personal possessions cover';

export function PolicyInformationWidget({ policy, policyModalCopyText }: PolicyInformationWidgetProps): JSX.Element {
  const {
    excesses,
    endorsements,
    addonDetails,
    specifiedItems,
    coverType,
    policyNumber: policyReference,
    postcode,
    county,
    house,
    street,
    locality,
    inceptionDate,
    endDate,
    buildingsSumInsured,
    contentsSumInsured,
    outbuildingsSumInsured,
    personalPossessionsSumInsured
  } = policy;

  const address = createAddress({ county, house, street, locality });

  const { addons } = addonDetails;
  const {
    coverDetails: coverDetailsCopyText,
    sumsInsured: sumsInsuredCopyText,
    excesses: excessesCopyText,
    endorsements: endorsementsCopyText,
    specifiedItems: specifiedItemsCopyText,
    addons: addonsCopyText
  } = policyModalCopyText;

  const formattedEndDate = dayjs(endDate.split('T')[0]).format(dateFormat);

  const coverDetailsCard = createTableCard({
    items: [
      [policyReferenceKey, policyReference],
      [policyTypeKey, coverType],
      [addressKey, `${address}, ${postcode}`],
      [startDateKey, dayjs(inceptionDate.split('T')[0]).format(dateFormat)],
      [endDateKey, formattedEndDate]
    ],
    headerNames: [],
    heading: <h2>{coverDetailsCopyText.heading}</h2>,
    body: coverDetailsCopyText.body
  });

  const { table: sumsInsuredTable, heading: sumsInsuredHeading, body: sumsInsuredBody } = sumsInsuredCopyText;
  const sumsInsuredTableHeaders = (sumsInsuredTable && sumsInsuredTable.primaryTableHeaders) || [];
  const sumsInsuredCard = createTableCard({
    items: [
      [buildingsKey, `£${buildingsSumInsured.toLocaleString()}`],
      [outbuildingsKey, `£${outbuildingsSumInsured.toLocaleString()}`],
      [contentsKey, `£${contentsSumInsured.toLocaleString()}`],
      [personalPossessionsKey, `£${personalPossessionsSumInsured.toLocaleString()}`]
    ],
    headerNames: sumsInsuredTableHeaders,
    heading: <h2>{sumsInsuredHeading}</h2>,
    body: sumsInsuredBody
  });

  const excessesCard = createExcessesCard(excesses, excessesCopyText);
  const endorsementsCard = createDetailsCard({
    items: endorsements,
    heading: <h2>{endorsementsCopyText.heading}</h2>,
    body: endorsementsCopyText.body,
    className: 'hp-cover-details'
  });

  const specifiedTableHeaders =
    (specifiedItemsCopyText.table && specifiedItemsCopyText.table.primaryTableHeaders) || [];
  let specifiedItemsCard: JSX.Element | null = null;
  if (specifiedItems.length) {
    const specifiedItemsRows = createSpecifiedItemsRows(specifiedItems);
    specifiedItemsCard = createTableCard({
      items: specifiedItemsRows,
      headerNames: specifiedTableHeaders,
      heading: <h2>{specifiedItemsCopyText.heading}</h2>,
      body: specifiedItemsCopyText.body
    });
  }

  let addonsCard: JSX.Element | null = null;
  if (addons.length) {
    addonsCard = createDetailsCard({
      items: addons,
      heading: <h2>{addonsCopyText.heading}</h2>,
      body: addonsCopyText.body,
      className: 'hp-cover-details'
    });
  }

  return (
    <>
      {coverDetailsCard}
      {sumsInsuredCard}
      {excessesCard}
      {endorsementsCard}
      {specifiedItemsCard}
      {addonsCard}
    </>
  );
}

interface CreateTableCardProps {
  body: string;
  headerNames: string[];
  heading: JSX.Element;
  items: string[][];
}

function createTableCard({ body, headerNames, heading, items }: CreateTableCardProps) {
  const tableWidget = <TableWidget headerNames={headerNames} rows={items} />;
  return createPolicyWidgetCard({
    heading,
    body,
    policyItemDetails: <>{tableWidget}</>
  });
}

function createExcessesCard(excesses: Excesses, excessesCopyText: HeadingAndBody): JSX.Element {
  const compulsoryExcessesTableHeaders = (excessesCopyText.table && excessesCopyText.table.primaryTableHeaders) || [];
  const voluntaryExcessesTableHeaders = (excessesCopyText.table && excessesCopyText.table.secondaryTableHeaders) || [];
  const { compulsoryExcessesTable, voluntaryExcessesTable } = createExcessesTables(
    excesses,
    compulsoryExcessesTableHeaders,
    voluntaryExcessesTableHeaders
  );
  const excessesCard = createPolicyWidgetCard({
    heading: <h2>{excessesCopyText.heading}</h2>,
    body: excessesCopyText.body,
    policyItemDetails: (
      <>
        {compulsoryExcessesTable}
        {voluntaryExcessesTable}
      </>
    )
  });

  return excessesCard;
}

function createSpecifiedItemsRows(specifiedItems: SpecifiedItem[]): string[][] {
  const specifiedItemsRows: string[][] = [];
  for (const item of specifiedItems) {
    const row = [item.description, `${item.isAwayFromHomeCover ? 'Yes' : 'No'}`, `£${item.amount.toFixed(2)}`];
    specifiedItemsRows.push(row);
  }

  return specifiedItemsRows;
}
